import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Lock, LogIn } from 'lucide-react';
import axios from 'axios';
import api, { login, storeSession } from '../../utils/authUtils';

const PageContainer = styled.div`
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  padding: 12px;
`;

const Card = styled.div`
  max-width: 400px;
  width: 100%;
  background-color: white;
  padding: 2rem;
  border-radius: 0px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const Title = styled.h2`
  margin-top: 0;
  text-align: center;
  font-size: 1.875rem;
  font-weight: 800;
  color: #1f2937;
`;

const Subtitle = styled.p`
  margin-top: 0.5rem;
  text-align: center;
  font-size: 0.875rem;
  color: #4b5563;
`;

const Form = styled.form`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  appearance: none;
  border: 1px solid #d1d5db;
  border-radius: 0px;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  width: 100%;
  color: #1f2937;

  &:focus {
    outline: none;
    border-color: #47ABDB;
    box-shadow: 0 0 0 3px rgba(71, 171, 219, 0.15);
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  border: 1px solid #f87171;
  color: #b91c1c;
  padding: 0.75rem;
  border-radius: 0px;
  font-size: 0.875rem;
`;

const Button = styled.button`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0px;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  background-color: #47ABDB;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StaticEmail = styled.div`
  font-size: 0.875rem;
  color: #1f2937;
  background-color: #f3f4f6;
  border-radius: 0px;
  padding: 0.5rem 0.75rem;
`;

// Landing page for a speaker invitation link. The same link works whether the
// account already exists or not - GET /api/speaker-invitations/{token} tells
// us which form to show. Setting a password (new account) auto-logs in;
// logging in (existing account) confirms the invitation separately.
const SpeakerInvitationLanding = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);
  const token = new URLSearchParams(location.search).get('token');

  const [status, setStatus] = useState('checking'); // checking | needs-password | needs-login | invalid
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Pulled out of the mount effect so it can also be re-run after a failed
  // activation (see handleCreatePassword) - the same live check that decides
  // which form to show in the first place is the right way to recover from
  // "this token's account changed state since the page loaded" (someone
  // clicked the same link/button twice, another tab finished first, or a
  // browser restored this page from back/forward cache after acceptance).
  const checkStatus = () => {
    if (!token) {
      setStatus('invalid');
      setError(t('speaker.invite.err.missingToken', 'This invitation link is missing a token.'));
      return;
    }
    return axios.get(`${process.env.REACT_APP_API_URL}/api/speaker-invitations/${token}`)
      .then((response) => {
        setEmail(response.data.email);
        setStatus(response.data.needsPassword ? 'needs-password' : 'needs-login');
      })
      .catch(() => {
        setStatus('invalid');
        setError(t('speaker.invite.err.invalid', 'This invitation link is invalid or has expired. Please contact the event organizer for a new link.'));
      });
  };

  useEffect(() => { checkStatus(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreatePassword = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError(t('speaker.invite.err.minLength', 'Password must be at least 8 characters.'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('speaker.invite.err.mismatch', 'Passwords do not match.'));
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/speaker-invitations/${token}/activate`, { password });
      storeSession(response.data);
      // Own confirmation page (SpeakerInviteConfirmation.js) - their
      // AI-researched, admin-reviewed profile already lives on their Account,
      // but they've never actually seen it themselves, so this is where they
      // review/correct it before it's shown publicly.
      navigate('/speaker/invite/confirmed', { state: { eventName: response.data.eventName, eventNameEN: response.data.eventNameEN } });
    } catch (err) {
      console.error('Invitation activation error:', err);
      setError(err.response?.data || t('speaker.invite.err.activate', 'Failed to set up your account. Please try again or request a new invitation.'));
      // E.g. this exact link/button was submitted twice (a genuine double
      // click, or a stale tab resubmitting after the account was already
      // activated elsewhere) - re-sync to whatever's actually true now
      // instead of leaving a dead-end error that retrying can't fix.
      checkStatus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(email, password);
      let eventName;
      let eventNameEN;
      try {
        const { data } = await api.post(`/api/speaker-invitations/${token}/confirm`);
        eventName = data.eventName;
        eventNameEN = data.eventNameEN;
      } catch (confirmError) {
        // Non-fatal: they're logged in either way, this is just bookkeeping.
        console.error('Failed to confirm invitation:', confirmError);
      }
      // Same confirmation page as a new account - a returning speaker
      // usually has nothing new to review, but they still get an explicit
      // receipt that this specific invitation went through, not silence.
      navigate('/speaker/invite/confirmed', { state: { eventName, eventNameEN } });
    } catch (err) {
      console.error('Invitation login error:', err);
      setError(t('auth.invalidCredentials', 'Invalid credentials or server error. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  if (status === 'checking') {
    return (
      <PageContainer>
        <Card>
          <Subtitle>{t('speaker.invite.checking', 'Checking your invitation…')}</Subtitle>
        </Card>
      </PageContainer>
    );
  }

  if (status === 'invalid') {
    return (
      <PageContainer>
        <Card>
          <Title>{t('speaker.invite.notFound', 'Invitation Not Found')}</Title>
          <ErrorMessage style={{ marginTop: '1rem' }}>{error}</ErrorMessage>
        </Card>
      </PageContainer>
    );
  }

  if (status === 'needs-password') {
    return (
      <PageContainer>
        <Card>
          <Title>{t('speaker.invite.setup.title', 'Set Up Your Speaker Account')}</Title>
          <Subtitle>{t('speaker.invite.setup.subtitle', 'Create a password for {email} to continue', { email })}</Subtitle>
          <Form onSubmit={handleCreatePassword}>
            <Input
              type="password"
              required
              minLength={8}
              placeholder={t('speaker.invite.newPassword', 'New Password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Input
              type="password"
              required
              minLength={8}
              placeholder={t('speaker.invite.confirmPassword', 'Confirm Password')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <Button type="submit" disabled={isLoading}>
              <Lock size={20} />
              {isLoading ? t('speaker.invite.settingUp', 'Setting up…') : t('speaker.invite.setPassword', 'Set Password & Continue')}
            </Button>
          </Form>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Card>
        <Title>{t('speaker.invite.back.title', 'Welcome Back')}</Title>
        <Subtitle>{t('speaker.invite.back.subtitle', 'Log in to continue with your speaker invitation')}</Subtitle>
        <Form onSubmit={handleLogin}>
          <StaticEmail>{email}</StaticEmail>
          <Input
            type="password"
            required
            placeholder={t('auth.password', 'Password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <Button type="submit" disabled={isLoading}>
            <LogIn size={20} />
            {isLoading ? t('speaker.invite.loggingIn', 'Logging in…') : t('speaker.invite.loginContinue', 'Log In & Continue')}
          </Button>
        </Form>
      </Card>
    </PageContainer>
  );
};

export default SpeakerInvitationLanding;
